/**
 * EnquiryQuickForm — fast-entry dialog (Spec §7).
 *
 * Optimised for phone triage: minimal required fields (subject +
 * contact identity or "unknown caller"), inline client lookup with
 * possible-match warnings, category picker with defaults.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, User, UserX, Sparkles, AlertCircle, Mic } from "lucide-react";
import EnquiryDictate from "@/components/enquiries/EnquiryDictate";
import { CustomerStatusBadge } from "@/components/enquiries/CustomerStatus";


const CHANNELS = [
  { key: "phone",  label: "Phone" },
  { key: "email",  label: "Email" },
  { key: "walkin", label: "Walk-in" },
  { key: "sms",    label: "SMS" },
  { key: "website",label: "Website" },
  { key: "other",  label: "Other" },
];


const pad2 = (n) => String(n).padStart(2, "0");
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const toIso = (local) => (local ? new Date(local).toISOString() : null);

function initialForm(e) {
  const c = e?.contact || {};
  return {
    is_new_enquiry: e ? e.is_new_enquiry !== false : true,
    source_channel: e?.source_channel || "phone",
    subject: e?.subject || "",
    message: e?.message || "",
    service_of_interest: e?.service_of_interest || "",
    category_id: e?.category_id || "",
    priority: e?.priority || "normal",
    action_required: e ? e.action_required !== false : true,
    contact: {
      name: c.name || "", mobile: c.mobile || "", email: c.email || "",
      preferred_channel: c.preferred_channel || "phone", unknown: !!c.unknown,
    },
    consent: { voicemail_ok: null, sms_ok: null, email_ok: null, do_not_contact: false, ...(e?.consent || {}) },
    assigned_to: e?.assigned_to || "",
    due_at: toLocalInput(e?.due_at),
    create_task: !e,
  };
}


export default function EnquiryQuickForm({ categories, categoryGroups, onClose, onCreated, enquiry }) {
  const { user } = useAuth();
  const isEdit = !!enquiry;
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [dictateOpen, setDictateOpen] = useState(false);
  const [lookupOn, setLookupOn] = useState(!isEdit);

  const [form, setForm] = useState(() => initialForm(enquiry));

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users");
        setUsers(r.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  function setContact(patch) {
    setForm((f) => ({ ...f, contact: { ...f.contact, ...patch } }));
  }

  function typeContact(patch) {
    setLookupOn(true);
    setContact(patch);
  }

  // Client lookup — kick off on each mobile/email/name typing pause.
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupBusy, setLookupBusy] = useState(false);
  const lookupTerm = (form.contact.name || form.contact.email || form.contact.mobile || "").trim();

  useEffect(() => {
    if (!lookupOn || form.contact.unknown) { setLookupResults([]); return; }
    if (lookupTerm.length < 2) { setLookupResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLookupBusy(true);
      try {
        const r = await api.get("/enquiries/client-lookup", { params: { q: lookupTerm } });
        if (!cancelled) setLookupResults(r.data || []);
      } catch {
        if (!cancelled) setLookupResults([]);
      } finally { if (!cancelled) setLookupBusy(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [lookupTerm, form.contact.unknown, lookupOn]);

  // Check for existing open enquiries from same contact.
  const checkDuplicates = useCallback(async () => {
    const email = form.contact.email?.trim();
    const mobile = form.contact.mobile?.trim();
    const needle = email || mobile;
    if (isEdit || !needle) { setDuplicateWarning(null); return; }
    try {
      const r = await api.get("/enquiries", { params: { q: needle } });
      const openOnes = (r.data || []).filter((e) =>
        ["new", "assigned", "in_progress", "waiting_client", "waiting_internal", "scheduled"].includes(e.status),
      );
      setDuplicateWarning(openOnes.length > 0 ? openOnes.slice(0, 3) : null);
    } catch { setDuplicateWarning(null); }
  }, [form.contact.email, form.contact.mobile, isEdit]);

  useEffect(() => {
    const t = setTimeout(checkDuplicates, 500);
    return () => clearTimeout(t);
  }, [checkDuplicates]);

  function pickCandidate(c) {
    setContact({
      name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.display_name,
      email: c.email || "",
      mobile: c.phone || "",
      unknown: false,
    });
    setLookupResults([]);
  }

  // Merge dictated results into the form — always populate the message
  // with the raw transcript (it's the fullest record), and fill any
  // extracted contact / subject / category fields the therapist hasn't
  // typed themselves yet. Never overwrite existing typed values.
  function applyDictation({ transcript, extracted }) {
    setForm((f) => {
      const next = { ...f, contact: { ...f.contact } };
      // Message: prefer LLM's tidy paraphrase if given, else raw transcript.
      const msgFromLlm = (extracted?.message || "").trim();
      const msg = msgFromLlm || (transcript || "").trim();
      if (msg && !next.message?.trim()) next.message = msg;

      if (extracted?.subject && !next.subject?.trim()) next.subject = extracted.subject;
      if (extracted?.service_of_interest && !next.service_of_interest?.trim()) {
        next.service_of_interest = extracted.service_of_interest;
      }
      if (extracted?.priority) next.priority = extracted.priority;
      if (typeof extracted?.is_new_enquiry === "boolean") {
        next.is_new_enquiry = extracted.is_new_enquiry;
      }

      // Contact fields — only fill blanks.
      if (!next.contact.unknown) {
        if (extracted?.name && !next.contact.name?.trim()) next.contact.name = extracted.name;
        if (extracted?.mobile && !next.contact.mobile?.trim()) next.contact.mobile = extracted.mobile;
        if (extracted?.email && !next.contact.email?.trim()) next.contact.email = extracted.email;
        if (extracted?.preferred_channel) next.contact.preferred_channel = extracted.preferred_channel;
      }
      return next;
    });
    setDictateOpen(false);
    toast.success("Enquiry pre-filled — review before saving");
  }

  async function submit() {
    if (!form.subject.trim()) return toast.error("Subject is required");
    if (!form.contact.unknown && !form.contact.name && !form.contact.email && !form.contact.mobile) {
      return toast.error("Add contact details or mark as unknown caller");
    }
    setSaving(true);
    try {
      if (isEdit) {
        const init = initialForm(enquiry);
        const patch = {};
        for (const k of Object.keys(form)) {
          if (k !== "create_task" && JSON.stringify(form[k]) !== JSON.stringify(init[k])) patch[k] = form[k];
        }
        if ("assigned_to" in patch) patch.assigned_to = patch.assigned_to || null;
        if ("category_id" in patch) patch.category_id = patch.category_id || null;
        if ("due_at" in patch) patch.due_at = toIso(patch.due_at);
        if (Object.keys(patch).length === 0) { toast("No changes to save"); onClose(); return; }
        const r = await api.patch(`/enquiries/${enquiry.id}`, patch);
        toast.success("Enquiry updated");
        onCreated?.(r.data);
        return;
      }
      const payload = { ...form };
      // Coerce empty strings to null for optional fields.
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.category_id) payload.category_id = null;
      payload.due_at = toIso(payload.due_at);
      const r = await api.post("/enquiries", payload);
      toast.success("Enquiry saved");
      onCreated?.(r.data);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  const staff = useMemo(() => users.filter((u) => !u.disabled), [users]);
  const groups = Object.keys(categoryGroups || {}).sort();

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={isEdit ? "enquiry-edit-form" : "enquiry-quick-form"}>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit enquiry" : "New enquiry"}</DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit
              ? "Add details you've remembered or correct anything. Every change is recorded in the Audit tab."
              : "Capture the message. Add contact details when you can, or mark as unknown caller and continue."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Voice-to-Enquiry: dictate a phone message, let Whisper pre-fill the form. */}
          {isEdit ? null : !dictateOpen ? (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDictateOpen(true)}
                className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
                data-testid="enquiry-dictate-open"
              >
                <Mic className="h-3.5 w-3.5 mr-1.5" /> Dictate phone message
              </Button>
            </div>
          ) : (
            <EnquiryDictate
              onApply={applyDictation}
              onClose={() => setDictateOpen(false)}
            />
          )}

          {/* Channel + new/existing toggle */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Channel</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {CHANNELS.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setForm({ ...form, source_channel: c.key })}
                    className={`text-xs px-2 py-1 rounded-md border ${form.source_channel === c.key ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
                    data-testid={`enquiry-channel-${c.key}`}
                  >{c.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-2xs">Enquiry type</Label>
              <div className="flex gap-1 mt-1">
                <button
                  onClick={() => setForm({ ...form, is_new_enquiry: true })}
                  className={`text-xs px-2 py-1 rounded-md border flex-1 ${form.is_new_enquiry ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
                  data-testid="enquiry-type-new"
                >New enquiry</button>
                <button
                  onClick={() => setForm({ ...form, is_new_enquiry: false })}
                  className={`text-xs px-2 py-1 rounded-md border flex-1 ${!form.is_new_enquiry ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
                  data-testid="enquiry-type-existing"
                >Existing client</button>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500 font-medium">Contact</Label>
              <label className="text-xs text-zinc-600 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.contact.unknown}
                  onChange={(e) => setContact({ unknown: e.target.checked })}
                  data-testid="enquiry-contact-unknown"
                />
                <UserX className="h-3.5 w-3.5" /> Unknown caller
              </label>
            </div>
            {!form.contact.unknown && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Name"
                    value={form.contact.name}
                    onChange={(e) => typeContact({ name: e.target.value })}
                    data-testid="enquiry-contact-name"
                  />
                  <Input
                    placeholder="Mobile"
                    value={form.contact.mobile}
                    onChange={(e) => typeContact({ mobile: e.target.value })}
                    data-testid="enquiry-contact-mobile"
                  />
                  <Input
                    placeholder="Email"
                    value={form.contact.email}
                    onChange={(e) => typeContact({ email: e.target.value })}
                    data-testid="enquiry-contact-email"
                  />
                </div>
                {lookupBusy && <div className="text-2xs text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Looking up…</div>}
                {lookupResults.length > 0 && (
                  <div className="border border-blue-200 bg-blue-50 rounded-md p-2 space-y-1" data-testid="enquiry-lookup-results">
                    <div className="text-2xs text-blue-900 flex items-center gap-1 font-medium">
                      <Sparkles className="h-3 w-3" /> Possible matches
                    </div>
                    {lookupResults.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => pickCandidate(c)}
                        className="w-full text-left text-xs bg-white hover:bg-blue-100 rounded px-2 py-1 border border-blue-100 flex items-center gap-2"
                        data-testid={`enquiry-lookup-${c.id}`}
                      >
                        <User className="h-3.5 w-3.5 text-blue-700" />
                        <b>{c.display_name}</b>
                        {c.email && <span className="text-zinc-500">{c.email}</span>}
                        {c.phone && <span className="text-zinc-500">· {c.phone}</span>}
                        {c.customer_status
                          ? <CustomerStatusBadge status={c.customer_status} className="ml-auto" />
                          : <span className="ml-auto text-2xs uppercase tracking-wider text-blue-800">{c.source}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {duplicateWarning && (
                  <div className="border border-amber-300 bg-amber-50 rounded-md p-2 text-xs text-amber-900 flex items-start gap-1.5" data-testid="enquiry-duplicate-warning">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div>
                      This contact already has <b>{duplicateWarning.length}</b> open enquir{duplicateWarning.length === 1 ? "y" : "ies"}:
                      <ul className="ml-3 mt-0.5 list-disc">
                        {duplicateWarning.map((d) => <li key={d.id}>{d.subject}</li>)}
                      </ul>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 text-xs">
                  <label>
                    Preferred channel:
                    <select
                      value={form.contact.preferred_channel}
                      onChange={(e) => setContact({ preferred_channel: e.target.value })}
                      className="ml-1 border border-zinc-300 rounded px-1 py-0.5"
                      data-testid="enquiry-preferred-channel"
                    >
                      <option value="phone">phone</option>
                      <option value="email">email</option>
                      <option value="sms">sms</option>
                      <option value="other">other</option>
                    </select>
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={form.consent.voicemail_ok === true}
                      onChange={(e) => setForm({ ...form, consent: { ...form.consent, voicemail_ok: e.target.checked || null } })}
                      data-testid="enquiry-consent-voicemail"
                    />
                    OK to leave voicemail
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Category + subject */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Category</Label>
              <select
                value={form.category_id}
                onChange={(e) => {
                  const cat = categories.find((c) => c.id === e.target.value);
                  setForm({
                    ...form,
                    category_id: e.target.value,
                    priority: cat?.default_priority || form.priority,
                  });
                }}
                className="mt-1 h-9 w-full px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="enquiry-category"
              >
                <option value="">— Select —</option>
                {groups.map((g) => (
                  <optgroup key={g} label={g}>
                    {(categoryGroups[g] || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-2xs">Priority</Label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="mt-1 h-9 w-full px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="enquiry-priority"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-2xs">Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="e.g. Prospective client - Exercise Physiology"
              data-testid="enquiry-subject"
            />
          </div>
          <div>
            <Label className="text-2xs">Message / notes</Label>
            <Textarea
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="What did they say? What do they want?"
              data-testid="enquiry-message"
            />
          </div>
          <div>
            <Label className="text-2xs">Service of interest</Label>
            <Input
              value={form.service_of_interest}
              onChange={(e) => setForm({ ...form, service_of_interest: e.target.value })}
              placeholder="Exercise Physiology, Clinical Pilates, etc."
              data-testid="enquiry-service"
            />
          </div>

          {/* Action */}
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500 font-medium">Action</Label>
              <label className="text-xs text-zinc-600 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.action_required}
                  onChange={(e) => setForm({ ...form, action_required: e.target.checked })}
                  data-testid="enquiry-action-required"
                />
                Action required
              </label>
            </div>
            {form.action_required && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-2xs">Assign to</Label>
                    <select
                      value={form.assigned_to}
                      onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                      className="mt-1 h-9 w-full px-2 border border-zinc-300 rounded-md text-sm bg-white"
                      data-testid="enquiry-assign-to"
                    >
                      <option value="">— Unassigned —</option>
                      <option value={user?.id}>{`Me (${user?.full_name || user?.email})`}</option>
                      {staff.filter((s) => s.id !== user?.id).map((s) => (
                        <option key={s.id} value={s.id}>{`${s.full_name || s.email} · ${s.role}`}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-2xs">Due</Label>
                    <Input
                      type="datetime-local"
                      value={form.due_at}
                      onChange={(e) => setForm({ ...form, due_at: e.target.value })}
                      data-testid="enquiry-due-at"
                    />
                  </div>
                </div>
                {!isEdit && (
                  <label className="text-xs text-zinc-600 inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.create_task}
                      onChange={(e) => setForm({ ...form, create_task: e.target.checked })}
                      data-testid="enquiry-create-task"
                    />
                    Also create a linked task in Tasks
                  </label>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="enquiry-form-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-zinc-950 text-white"
            data-testid="enquiry-save"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : isEdit ? "Save changes" : "Save enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
